import { t } from '@lingui/core/macro';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';

import { TRAFFIC_TYPES, type VisitTrafficTrendType } from '@/api/common';
import Flare, {
  Area,
  Axis,
  type FlareSeriesOptions,
  Legend,
  Notes,
  Tooltip,
} from '@/components/Flare';
import type { TooltipPoint } from '@/components/Flare/types';
import { usePageFilterContext } from '@/components/page';
import {
  AUDIENCE_PRIMARY,
  BOT_PRIMARY,
  TAM_PRIMARY,
  UNKNOWN_PRIMARY,
  VISITS_PRIMARY,
} from '@/constants/colors';
import { MonthDateShortFormat, MonthDateYearFormat } from '@/constants/formats';
import type SolAPIError from '@/error/SolAPIError';
import { getUTCTime } from '@/helper/dateHelper';
import { numberFormat } from '@/helper/numberFormatter';

export type VisitBreakdownTrendChartProps = {
  mode?: 'stack' | 'percent';
  data: VisitTrafficTrendType[] | undefined;
  isLoading?: boolean;
  error?: SolAPIError | null;
  description?: string;
  tooltipRowLink: (item: TooltipPoint) => string | undefined;
};

const VisitBreakdownTrendChart: React.FC<VisitBreakdownTrendChartProps> = ({
  mode = 'stack',
  data,
  isLoading,
  error,
  description = t`A trend chart showing the number of visits by traffic type: Audience or TAM, Other, Bot, and Unknown`,
  tooltipRowLink,
}) => {
  const {
    pageFilters: { audience },
  } = usePageFilterContext();

  const seriesData: FlareSeriesOptions[] | undefined = useMemo(() => {
    if (!data) return undefined;
    return [
      {
        id: TRAFFIC_TYPES.UNRESOLVED,
        name: t`Unknown`,
        zIndex: 5,
        data: data.map((d) => ({ date: d.sundayOfWeek, amount: d.unresolvedVisits })),
      },
      {
        id: TRAFFIC_TYPES.BOT,
        name: t`Bot`,
        zIndex: 5,
        data: data.map((d) => ({ date: d.sundayOfWeek, amount: d.botVisits })),
      },
      {
        id: TRAFFIC_TYPES.OTHER,
        name: t`Other Companies`,
        zIndex: 5,
        data: data.map((d) => ({ date: d.sundayOfWeek, amount: d.otherVisits })),
      },
      {
        id: TRAFFIC_TYPES.TARGET,
        name: audience?.isTAM ? t`TAM` : (audience?.name ?? t`Audience`),
        zIndex: 5,
        data: data.map((d) => ({ date: d.sundayOfWeek, amount: d.targetVisits })),
      },
    ];
  }, [data, audience?.isTAM, audience?.name]);

  return (
    <Flare
      data={seriesData}
      width="auto"
      height="auto"
      colors={[
        UNKNOWN_PRIMARY,
        BOT_PRIMARY,
        VISITS_PRIMARY,
        audience?.isTAM ? TAM_PRIMARY : AUDIENCE_PRIMARY,
      ]}
      parseX={getUTCTime}
      description={description}
      isLoading={isLoading}
      error={error}
    >
      <Legend reversed />
      <Area x="date" y="amount" position={mode} fillOpacity={0.85} />
      <Axis
        position="left"
        labelFormat={(item) =>
          numberFormat(mode === 'percent' ? (item.value as number) / 100 : item.value, {
            isPercent: mode === 'percent',
            compact: mode !== 'percent',
          })
        }
        title={t`Visits`}
      />
      <Axis
        type="datetime"
        position="bottom"
        crosshair="line"
        labelFormat={(item) => dayjs(item.value).utc().format(MonthDateShortFormat)}
      />
      <Tooltip
        shared
        titleFormat={(point) => dayjs(point?.x).format(MonthDateYearFormat)}
        rowValueFormat={(point) => numberFormat(point?.y)}
        rowSecondaryValueFormat={(point) => {
          if (point != null && point.total && point.y) {
            return t`(${numberFormat(point.y / point.total, { isPercent: true, precision: 1 })})`;
          }
          return undefined;
        }}
        rowValueLink={tooltipRowLink}
        showTotalRow
        unstable_hasRouterContext={false}
      />
      <Notes />
    </Flare>
  );
};

export default VisitBreakdownTrendChart;
